// Disabling linting for clicks on the backdrop, users with keyboard controls will interact with close buttons.
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

/**
  Use when an action taken by a user needs to be interrupted to confirm or cancel an action.
 */
const Modal = ({
  toggle,
  isOpen,
  hasTitle,
  hasFooter,
  classList,
  headingTag = "h4",
  titleText = "",
  cancelButtonText = "Cancel",
  submitButtonText = "Submit",
  sendClick,
  children
}) => {
  /**
   * Renders the title based on the heading tag passed in
   * @example
   * props.headingTag = "h4";
   * renderTitle();
   * <h4>titleText</h4>
   *
   * @returns {HTMLElement} If using a title, returns a heading element with the tag provided
   * @returns {Null} Returns null if a title is not being used
   */
  const renderTitle = () => {
    // JSX requires a capital letter variable to use as a tag
    const Tag = headingTag;
    if (hasTitle) {
      return <Tag className="modal-title">{titleText}</Tag>;
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
    }

    return () => {
      document.body.style.overflow = "visible";
    };
  }, [isOpen]);

  /**
   * Clicking the primary button sends the click event to the parent
   * and then closes the modal.
   *
   * @param {Event} e
   */
  const handleClick = (e) => {
    sendClick(e);
    toggle();
  };

  const toggleDisplay = isOpen ? "block" : "none";
  const wrapperClass = isOpen ? "modal-open" : "";
  const animationClass = isOpen ? "in" : "";
  const titleDisplayClass = hasTitle ? "" : "modal--no-title";
  const footerDisplayClass = hasFooter ? "" : "modal--no-footer";

  return (
    <div id="modal__root">
      {isOpen && (
        <div className={`modal__wrapper ${wrapperClass} ${classList}`}>
          <div
            className={`modal fade ${animationClass}`}
            tabIndex="-1"
            role="dialog"
            style={{ display: toggleDisplay }}
          >
            <div className="modal-dialog" role="document">
              <div className={`modal-content ${titleDisplayClass} ${footerDisplayClass}`}>
                <div className="modal-header">
                  <button type="button" className="close" aria-label="Close" onClick={toggle}>
                    <span aria-hidden="true">
                      <FontAwesomeIcon icon={faTimes} />
                    </span>
                  </button>
                  {renderTitle()}
                </div>
                <div className="modal-body">{children}</div>
                {hasFooter ? (
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-normal btn-ghost-orange"
                      onClick={toggle}
                    >
                      {cancelButtonText}
                    </button>
                    <button
                      type="button"
                      className="btn btn-normal btn-primary"
                      onClick={(e) => handleClick(e)}
                    >
                      {submitButtonText}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div className={`modal-backdrop fade ${animationClass}`} onClick={toggle} />
          </div>
        </div>
      )}
    </div>
  );
};

Modal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  hasTitle: PropTypes.bool.isRequired,
  hasFooter: PropTypes.bool.isRequired,
  classList: PropTypes.string,
  headingTag: PropTypes.string,
  titleText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
  /** This function sends the click event back up to the parent, so the form can handle the submit button. */
  sendClick: PropTypes.func.isRequired,
  /** Add the body content of the modal as child(ren) element(s). */
  children: PropTypes.node.isRequired
};

export default Modal;
